import type { ChatToolUseContent, ClientToolResult } from "../ChatRecord";

import type { ApprovalMode } from "./approvalModes";
import { needsApproval, resolveApprovalMode } from "./approvalModes";
import type { ClientTool, ClientToolDeps } from "./ClientTools";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PendingToolApproval {
  toolUse: ChatToolUseContent;
  tool: ClientTool;
}

export interface BatchRunnerCallbacks {
  /** Called whenever the current pending approval changes (including to null). */
  onPendingApprovalChanged: (pending: PendingToolApproval | null) => void;

  /** Called when all tools in the batch have resolved and results are ready to submit. */
  onBatchComplete: (sessionId: string, results: ClientToolResult[]) => void;
}

export interface BatchRunConfig {
  sessionId: string;
  toolUses: ChatToolUseContent[];
  autoEnabled: boolean;
  userPreferences: Record<string, ApprovalMode> | null | undefined;
  deps: ClientToolDeps;
  resolveClientTool: (toolName: string) => ClientTool | undefined;
}

// ---------------------------------------------------------------------------
// Result helpers
// ---------------------------------------------------------------------------

function buildToolResult(
  toolUse: ChatToolUseContent,
  result: { response: Record<string, unknown>; success: boolean },
  runtimeMs: number,
): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: runtimeMs,
    status: result.success ? "success" : "error",
    output: result.response,
  };
}

function buildDeclinedResult(toolUse: ChatToolUseContent): ClientToolResult {
  return {
    tool_name: toolUse.tool_name,
    tool_use_id: toolUse.tool_use_id,
    runtime_ms: 0,
    status: "error",
    output: { message: "User declined to execute this tool." },
  };
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

/**
 * Pure state machine for batch client-tool execution with approval gating.
 *
 * Manages the triage (auto-execute vs queue for approval), approval queue,
 * result accumulation, and "batch complete" detection. No React, no hooks,
 * no DOM — all interaction with the component tree happens via callbacks.
 *
 * The hook `useClientToolExecution` creates a runner and acts as a thin
 * adapter that bridges React context (services, navigation, state) to
 * the runner's lifecycle methods.
 */
export class ClientToolBatchRunner {
  private pendingQueue: PendingToolApproval[] = [];
  private accumulatedResults: ClientToolResult[] = [];
  private totalToolCount = 0;
  private batchSessionId: string | null = null;
  private currentApproval: PendingToolApproval | null = null;
  private callbacks: BatchRunnerCallbacks;

  constructor(callbacks: BatchRunnerCallbacks) {
    this.callbacks = callbacks;
  }

  get pendingApproval(): PendingToolApproval | null {
    return this.currentApproval;
  }

  /**
   * Start processing a batch of tool uses. Auto-executes tools that don't
   * need approval, queues the rest. Fires `onPendingApprovalChanged` if any
   * tool needs approval, or `onBatchComplete` if all were auto-executed.
   */
  async startBatch(config: BatchRunConfig): Promise<void> {
    this.reset();
    this.totalToolCount = config.toolUses.length;
    this.batchSessionId = config.sessionId;

    const needsApprovalQueue: PendingToolApproval[] = [];

    for (const toolUse of config.toolUses) {
      const tool = config.resolveClientTool(toolUse.tool_name);

      if (!tool) {
        this.accumulatedResults.push(
          buildToolResult(
            toolUse,
            {
              response: {
                error: `Unknown client tool: ${toolUse.tool_name}`,
              },
              success: false,
            },
            0,
          ),
        );
        continue;
      }

      const mode = resolveApprovalMode(tool, config.userPreferences);

      if (needsApproval(mode, config.autoEnabled)) {
        needsApprovalQueue.push({ toolUse, tool });
      } else {
        await this.executeAndRecord(toolUse, tool, config.deps);
      }
    }

    if (needsApprovalQueue.length > 0) {
      this.pendingQueue = needsApprovalQueue.slice(1);
      this.currentApproval = needsApprovalQueue[0];
      this.callbacks.onPendingApprovalChanged(this.currentApproval);
    } else {
      this.completeBatch();
    }
  }

  /**
   * User approved the currently pending tool. Executes it, records the
   * result, and advances the queue. `deps` is passed at call time so the
   * hook can provide fresh values (e.g., current route for isOnVisualizerPage).
   */
  async approve(deps: ClientToolDeps): Promise<void> {
    if (!this.currentApproval) {
      return;
    }

    const { toolUse, tool } = this.currentApproval;
    await this.executeAndRecord(toolUse, tool, deps);
    this.advanceQueue();
  }

  /**
   * User declined the currently pending tool. Records a declined result
   * and advances the queue.
   */
  decline(): void {
    if (!this.currentApproval) {
      return;
    }

    this.accumulatedResults.push(
      buildDeclinedResult(this.currentApproval.toolUse),
    );
    this.advanceQueue();
  }

  /** Reset all internal state. */
  reset(): void {
    this.pendingQueue = [];
    this.accumulatedResults = [];
    this.totalToolCount = 0;
    this.batchSessionId = null;
    this.currentApproval = null;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private async executeAndRecord(
    toolUse: ChatToolUseContent,
    tool: ClientTool,
    deps: ClientToolDeps,
  ): Promise<void> {
    const inputParams = toolUse.input ?? {};
    try {
      const startTime = performance.now();
      const result = await tool.execute(inputParams, deps);
      const runtimeMs = Math.round(performance.now() - startTime);
      this.accumulatedResults.push(buildToolResult(toolUse, result, runtimeMs));
    } catch (error) {
      this.accumulatedResults.push(
        buildToolResult(
          toolUse,
          {
            response: {
              error: error instanceof Error ? error.message : String(error),
            },
            success: false,
          },
          0,
        ),
      );
    }
  }

  private advanceQueue(): void {
    const next = this.pendingQueue.shift();
    if (next) {
      this.currentApproval = next;
      this.callbacks.onPendingApprovalChanged(next);
    } else {
      this.currentApproval = null;
      this.callbacks.onPendingApprovalChanged(null);
      if (this.accumulatedResults.length === this.totalToolCount) {
        this.completeBatch();
      }
    }
  }

  private completeBatch(): void {
    if (this.batchSessionId) {
      this.callbacks.onBatchComplete(this.batchSessionId, [
        ...this.accumulatedResults,
      ]);
    }
  }
}
